/*
 * vms-slanted-wall-drag.js  (LANE B)
 * ---------------------------------------------------------------------------
 * The pointer + keyboard behaviour for the three drag handles on the "Your
 * build, to scale" elevation SVG of the slanted-wall page: slope angle, TV tilt
 * and TV height. It is a STANDALONE enhancement layer, exactly like
 * assets/vms-mast-art.js and assets/vms-elevation-scene.js: it knows nothing
 * about the solver, the SKUs, the pricing or the DOM ids inside the answer. It
 * talks to the page controller (assets/vms-slanted-wall.js) through ONE public
 * object and ONE custom event, and to the SVG only by the three fixed handle ids.
 *
 * WHY A SEPARATE FILE. The page (LANE A, assets/vms-slanted-wall.js) OWNS the
 * SVG: it renders it, it authors the handle elements and their aria, it holds
 * the state, it selects the pipe and it re-renders the whole answer on every
 * commit. This file adds nothing to that DOM and mutates no engine state. It
 * reads the scene, converts pointer and key gestures to numbers, calls the
 * page's cheap local PREVIEW during a move, and calls the page's authoritative
 * SETTER once on commit. That split is the whole reason two agents could build
 * the two files at once.
 *
 * THE CONTRACT THIS FILE IS BUILT TO (DOM + API), byte for byte:
 *   window.VMSSlantedPage = {
 *     getScene, setSlopeDeg, setTilt, setHeadDrop,
 *     previewSlope, previewTilt, previewHeadDrop
 *   }
 *   The page dispatches, after EVERY answer render (right where wireMast runs):
 *     ROOT.dispatchEvent(new CustomEvent('vmsw:rendered', { detail: getScene() }))
 *   getScene() returns scene inches + fit, y UP, P at (0,0), and:
 *     px = fit.ox + x * fit.s ,  py = fit.oy - y * fit.s
 *   The fields this file reads: svgId, fit {s, ox, oy}, slopeDeg, tiltDeg,
 *   tiltMax, standoff, holeCentreY, headDrop, headDropMin, headDropMax,
 *   headDropMaxAll (optional; falls back to headDropMax).
 *   Handle ids INSIDE the SVG (authored by the page, replaced every render):
 *     #vmsw-elev-h-slope  #vmsw-elev-h-tilt  #vmsw-elev-h-height
 *
 * UNITS AND PHASES (owner-approved drag spec 2026-08-29):
 *   - slope and tilt are in DEGREES; height is in INCHES.
 *   - MOVE calls previewSlope / previewTilt / previewHeadDrop at most once per
 *     animation frame. No re-render, no engine, no commit.
 *   - COMMIT (pointerup, keyup after a key run, Home/End immediately) calls the
 *     setter once and lets the page re-render, which re-attaches the handles
 *     through the 'vmsw:rendered' hook. A pointercancel restores the pre-drag
 *     value through the preview and does NOT commit.
 *   - Previews are APPROXIMATE by design (no head-frame swap, no re-solve).
 *     Commit makes it exact.
 *
 * THE FIT IS FROZEN FOR THE LENGTH OF A GESTURE. This is the analogue of the
 * elevation scene's frozen scale, and it is what keeps the handle under the
 * pointer. The emitted value is derived from fit.s and the drawn origin, and the
 * handle is drawn at value -> px through the SAME fit; those two are inverses of
 * each other only while fit is CONSTANT. The page only re-renders on COMMIT, not
 * on move, so fit cannot move mid-gesture anyway, but we still snapshot the scene
 * and its fit at pointerdown and use that snapshot for the whole gesture rather
 * than re-reading it per move. See gestureScene below.
 *
 * DRAGS ARE ABSOLUTE, NEVER INCREMENTAL, same discipline as vms-elevation-scene:
 *   - slope maps the pointer's BEARING about P straight to the slope angle (the
 *     same absolute mapping the step-1 angle handle already uses), so a clamped
 *     value cannot creep.
 *   - height is startDrop + (pointer travel in inches), absolute from the value
 *     captured at pointerdown.
 *   - tilt is a DELTA from the pointer bearing captured at pointerdown, added to
 *     the tilt captured at pointerdown, so grabbing the corner off-centre does
 *     not snap the angle.
 *
 * TOUCH CLAIMS ON THE PILL IMMEDIATELY. [TOUCH-FIX 2026-08-30] A touch that starts
 * on a handle is a drag, claimed at pointerdown, exactly like mouse and pen. The
 * old hold/slop dance (rest TOUCH_HOLD_MS within TOUCH_SLOP_PX before claiming) was
 * retired: a real finger drag moves more than a few px inside 130ms, so it was read
 * as a scroll and the pill highlighted but never moved (the owner's iPhone report,
 * 2026-08-30). The pills are small, so giving up scroll-that-starts-on-a-pill is
 * fine; a touch that starts anywhere else on the SVG still scrolls, because these
 * listeners are bound per handle. A non-passive touchstart on the handle calls
 * preventDefault at sequence start (the reliable iOS scroll trap, since WebKit's
 * touch-action on an SVG element is unreliable), and the non-passive touchmove keeps
 * calling preventDefault for the length of the claimed drag.
 *   [NOTE] The page CSS still scopes touch-action: none to .vmsw-elev__handle only
 *   (belt to the touchstart braces); a touch off a handle is untouched and scrolls.
 *   The TOUCH_HOLD_MS / TOUCH_SLOP_PX tunables and the pending-touch branches remain
 *   defined but inert (pointerdown never enters the pending path now). Settled
 *   2026-08-30, superseding the 2026-08-29 hold/slop decision.
 *
 * ANNOUNCE ON COMMIT, NOT PER MOVE. aria-valuenow is nudged during a move so a
 * screen reader that polls the slider sees a live number, but the page re-authors
 * the full aria (valuemin/max/now/valuetext from the copy keys) on every render,
 * which is the authoritative pass. This file never invents customer-facing copy.
 *
 * FOCUS SURVIVES THE RE-RENDER. Because a commit replaces the handle element, a
 * keyboard user would lose focus on every keystroke. If the committing handle
 * had focus, its id is re-focused after the next 'vmsw:rendered'. See refocusId.
 *
 * THE PURE GEOMETRY (free functions at module scope, so the Node harness lifts
 * them by name with extractFunction + vm, the way test_vms_slanted_page.js does):
 *   slopeDegFromPointer(px, py, fit)            -> clamped slope degrees (absolute)
 *   tiltDegFromPointer(px, py, fit, scene)      -> RAW pointer bearing degrees
 *                                                  about the drawn hole centre
 *   headDropFromPointer(y, startY, startDrop, fit) -> RAW inches (unclamped)
 *   clampSlope(deg) / clampTilt(deg, tiltMax) / clampHeadDrop(inches, min, max)
 *   normalizeAngleDelta(deg)
 * See the SIGNATURE NOTES at each function for what Lane C must know.
 */
(function () {
  'use strict';

  // =====================================================================
  // PURE GEOMETRY. No DOM, Math only, self-contained (nothing here reads a
  // module constant or calls another lifted function), so each lifts cleanly.
  // =====================================================================

  /**
   * Clamp with explicit numeric guards. Free helper for Lane C; the handlers use
   * the named clamps below.
   */
  function clampNum(v, lo, hi) {
    if (!(typeof v === 'number' && isFinite(v))) return lo;
    if (v < lo) return lo;
    if (v > hi) return hi;
    return v;
  }

  /**
   * slopeDegFromPointer(px, py, fit)
   * -------------------------------------------------------------------------
   * The slope angle, in degrees, for a pointer at SVG viewBox coordinates
   * (px, py), mapped ABSOLUTELY as a RAY that FOLLOWS THE POINTER (exactly like
   * the step-1 angle handle: no delta-from-grab, so the grip sits under the
   * finger and the visible ceiling follows the drag rather than mirroring it).
   *
   * [DIRECTION CONTRACT] Dragging the grip DOWN (toward the pipe) makes the
   * ceiling STEEPER; dragging it up toward horizontal makes it shallower. That
   * falls out directly: py grows downward, so a lower pointer has a smaller dy
   * (more negative), a larger -dy, and thus a larger atan2(-dy, -dx).
   *
   * Derivation. The scene has P at (0,0), y UP, and the fit gives px = ox + x*s,
   * py = oy - y*s; so in scene inches dx = (px-ox)/s and dy = (oy-py)/s. The
   * VISIBLE wall runs DOWN and to the LEFT of P (P is the plate point at the TOP
   * of the picture; the wall line is x = k*y with y negative below P, so x is
   * negative there). The wall RAY from P therefore points down-left, i.e. along
   * (-dx, -dy) for a down-left pointer, and the page defines the slope as
   * deg = atan2(pitch, 12) (assets/vms-slope.js pitchDegrees), which for a point
   * on that ray is atan2(-dy, -dx). So: pointer down-left at 63 gives 63, pointer
   * straight down gives 90 -> clamp 89, pointer straight left gives 0.
   *
   * OUT-OF-QUADRANT: the pointer can leave the drawn down-left quadrant during a
   * drag; rather than fold it (which mirrors the ceiling the wrong way, the very
   * bug this fixes) we clamp to the nearest limit. dy > 0 (pointer ABOVE the
   * horizontal through P) clamps to 0 (shallowest); dx > 0 (pointer RIGHT of P,
   * below the horizontal) clamps to 89 (steepest). Clamp 0..89 overall (89 is the
   * page's hard ceiling; at 90 the run is 0 and pitchFromDegrees blows up).
   * (Owner feedback / coordinator correction 2026-08-29.)
   */
  function slopeDegFromPointer(px, py, fit) {
    var dx = (px - fit.ox) / fit.s;
    var dy = (fit.oy - py) / fit.s;              // scene inches, y up
    if (dy > 0) return 0;                        // above the horizontal -> shallowest
    if (dx > 0) return 89;                       // right of P (and below) -> steepest
    var deg = Math.atan2(-dy, -dx) * 180 / Math.PI;   // wall ray points down-left
    if (deg < 0) deg = 0; else if (deg > 89) deg = 89;
    return deg;
  }

  /**
   * tiltDegFromPointer(px, py, fit, scene)
   * -------------------------------------------------------------------------
   * The RAW bearing of the pointer, in degrees, about the DRAWN hole centre
   * (scene.standoff, scene.holeCentreY), measured in viewBox px space (y DOWN,
   * as SVG rotate() sees it). It is NOT the final tilt: the tilt handler takes
   * newTilt = startTilt + (bearingNow - bearingAtGrab), then clampTilt and
   * rounds to whole degrees. Returning the raw bearing keeps this a pure,
   * single-pointer geometry function (monotonic and scale-correct, which is what
   * Lane C tests) while the delta-from-grab and the clamp live in the handler.
   *
   * The sign is right by construction: the page rotates the TV group by
   * rotate(+tiltDeg ...) (assets/vms-slanted-wall.js tvTiltTransform), which is
   * CLOCKWISE on screen; in a y-down frame atan2 also increases clockwise, so a
   * clockwise drag of the corner yields a positive bearing delta and a positive
   * tilt.
   *
   * SIGNATURE NOTE for Lane C: this returns a bearing, not a tilt. The delta and
   * clamp are applied by the handler with the grab reference; test this one for
   * bearing monotonicity and test clampTilt separately.
   */
  function tiltDegFromPointer(px, py, fit, scene) {
    var hcx = fit.ox + scene.standoff * fit.s;
    var hcy = fit.oy - scene.holeCentreY * fit.s;
    return Math.atan2(py - hcy, px - hcx) * 180 / Math.PI;
  }

  /**
   * headDropFromPointer(y, startY, startDrop, fit)
   * -------------------------------------------------------------------------
   * The RAW head drop, in inches, absolute from the value captured at
   * pointerdown: startDrop + (y - startY) / fit.s. DOWN is a LARGER drop, and
   * that falls out of the coordinate frame directly: py = oy - y*s, so moving
   * the pointer DOWN increases the viewBox y, and (y - startY) is then positive,
   * which increases the drop. No clamp here (the limits live in the scene, not in
   * this signature); the handler clamps with clampHeadDrop.
   *
   * SIGNATURE NOTE for Lane C: y and startY are in the SVG viewBox coordinate
   * space (the same space fit maps into), NOT raw clientY. The handler converts
   * the pointer's clientY to viewBox y with the SVG's own CTM before calling, so
   * fit.s (viewBox units per inch) is the correct divisor. Test with viewBox y
   * values; with a 1:1 fit (s = 1) it is the plain startDrop + (y - startY).
   */
  function headDropFromPointer(y, startY, startDrop, fit) {
    return startDrop + (y - startY) / fit.s;
  }

  /** Clamp a slope angle to the page's 0..89 window. */
  function clampSlope(deg) {
    if (!(typeof deg === 'number' && isFinite(deg))) return 0;
    if (deg < 0) return 0;
    if (deg > 89) return 89;
    return deg;
  }

  /** Clamp a tilt angle to 0..tiltMax (whole-degree rounding is the caller's). */
  function clampTilt(deg, tiltMax) {
    var m = (typeof tiltMax === 'number' && isFinite(tiltMax) && tiltMax > 0) ? tiltMax : 0;
    if (!(typeof deg === 'number' && isFinite(deg))) return 0;
    if (deg < 0) return 0;
    if (deg > m) return m;
    return deg;
  }

  /** Clamp a head drop to [min, max]; a missing bound is treated as open. */
  function clampHeadDrop(inches, min, max) {
    var lo = (typeof min === 'number' && isFinite(min)) ? min : -Infinity;
    var hi = (typeof max === 'number' && isFinite(max)) ? max : Infinity;
    if (!(typeof inches === 'number' && isFinite(inches))) return lo === -Infinity ? 0 : lo;
    if (inches < lo) return lo;
    if (inches > hi) return hi;
    return inches;
  }

  /** Wrap an angle delta to (-180, 180], so a bearing that crosses +/-180 does
   *  not jump by a full turn during a drag. */
  function normalizeAngleDelta(deg) {
    while (deg > 180) deg -= 360;
    while (deg < -180) deg += 360;
    return deg;
  }

  // =====================================================================
  // DOM GLUE. Everything below is impure (events, timers, the SVG). It reads
  // the pure functions above and the page API; it authors no answer DOM.
  // =====================================================================

  var ROOT = document.getElementById('vmsw-calc');
  if (!ROOT) return;                       // not our page

  // ---- interaction tunables (mirror vms-elevation-scene) -------------------
  // [TOUCH-FIX 2026-08-30] RETIRED but kept defined so the inert pending-touch branches still reference
  // them: touch now claims immediately at pointerdown (see down / touchstart). They no longer gate a drag.
  var TOUCH_HOLD_MS = 130;
  var TOUCH_SLOP_PX = 6;
  var KEY_IDLE_MS = 1200;                   // safety net for a keyup that never arrives
  var STEP = {
    slope:  { arrow: 1,    page: 5    },    // degrees
    tilt:   { arrow: 1,    page: 5    },    // degrees
    height: { arrow: 0.25, page: 1.25 }     // inches (0.25 in keyboard steps)
  };
  var HANDLE_IDS = {
    slope:  'vmsw-elev-h-slope',
    tilt:   'vmsw-elev-h-tilt',
    height: 'vmsw-elev-h-height'
  };

  // The scene from the most recent render, and the id we owe a re-focus to after
  // the next render (a commit replaced the focused handle element).
  var currentScene = null;
  var refocusId = null;
  // Per-handle teardown functions, so a re-attach cannot leak a stale timer or a
  // captured pointer onto a detached element.
  var teardowns = [];

  function api() { return window.VMSSlantedPage || null; }

  function num(v) { return typeof v === 'number' && isFinite(v) ? v : null; }

  // Convert a client (screen) point to the SVG's own viewBox coordinates. The
  // CTM path is exact through any CSS sizing or transform and correctly handles
  // preserveAspectRatio letterboxing; the rect+viewBox fallback is the naive
  // linear map for engines without getScreenCTM (and matches the step-1 angle
  // handle's own mapping).
  function clientToUser(svg, clientX, clientY) {
    try {
      if (svg.getScreenCTM && svg.createSVGPoint) {
        var ctm = svg.getScreenCTM();
        if (ctm) {
          var p = svg.createSVGPoint();
          p.x = clientX; p.y = clientY;
          var u = p.matrixTransform(ctm.inverse());
          return { x: u.x, y: u.y };
        }
      }
    } catch (e) { /* fall through to the rect map */ }
    var rect = svg.getBoundingClientRect();
    var vb = svg.viewBox && svg.viewBox.baseVal;
    var vbw = (vb && vb.width) ? vb.width : rect.width;
    var vbh = (vb && vb.height) ? vb.height : rect.height;
    return {
      x: rect.width ? (clientX - rect.left) / rect.width * vbw : 0,
      y: rect.height ? (clientY - rect.top) / rect.height * vbh : 0
    };
  }

  // The scene value a handle currently represents.
  function sceneValue(kind, scene) {
    if (kind === 'slope') return num(scene.slopeDeg);
    if (kind === 'tilt') return num(scene.tiltDeg);
    return num(scene.headDrop);
  }

  // The enforced [lo, hi] stops for a handle, from the scene.
  function sceneLimits(kind, scene) {
    if (kind === 'slope') return { lo: 0, hi: 89 };
    if (kind === 'tilt') {
      var m = num(scene.tiltMax); if (m == null || m < 0) m = 0;
      return { lo: 0, hi: m };
    }
    var lo = num(scene.headDropMin);
    var hi = num(scene.headDropMaxAll);
    if (hi == null) hi = num(scene.headDropMax);        // contract fallback
    return { lo: lo == null ? 0 : lo, hi: hi == null ? Infinity : hi };
  }

  // Call the page's cheap local preview for a kind.
  function preview(kind, v) {
    var a = api(); if (!a) return;
    if (kind === 'slope' && typeof a.previewSlope === 'function') a.previewSlope(v);
    else if (kind === 'tilt' && typeof a.previewTilt === 'function') a.previewTilt(v);
    else if (kind === 'height' && typeof a.previewHeadDrop === 'function') a.previewHeadDrop(v);
  }

  // Call the page's authoritative setter for a kind (this re-renders the answer).
  function commit(kind, v) {
    var a = api(); if (!a) return;
    if (kind === 'slope' && typeof a.setSlopeDeg === 'function') a.setSlopeDeg(v);
    else if (kind === 'tilt' && typeof a.setTilt === 'function') a.setTilt(v);
    else if (kind === 'height' && typeof a.setHeadDrop === 'function') a.setHeadDrop(v);
  }

  // The value to feed a preview, quantised per kind: whole degrees for tilt, raw
  // (clamped) for slope and height. Slope stays continuous so the rotation is
  // smooth; the page rounds for display.
  function previewValue(kind, raw, lim) {
    if (kind === 'tilt') return clampTilt(Math.round(raw), lim.hi);
    if (kind === 'slope') return clampSlope(raw);
    return clampHeadDrop(raw, lim.lo, lim.hi);
  }

  // The value to feed a setter on commit, same quantisation as the preview.
  function commitValue(kind, raw, lim) {
    return previewValue(kind, raw, lim);
  }

  // Nudge aria-valuenow so a polling AT hears the live number during a move. The
  // page re-authors the full aria on the next render.
  function nudgeAria(el, kind, v) {
    if (!el) return;
    var n = (kind === 'height') ? Math.round(v * 100) / 100 : Math.round(v);
    el.setAttribute('aria-valuenow', String(n));
  }

  // ---- one handle --------------------------------------------------------
  function wireHandle(kind, el, svg) {
    var stepDef = STEP[kind];

    // Gesture state, all cleared between gestures.
    var pendingId = null;      // touch waiting on the hold test
    var holdTimer = 0;
    var dragId = null;         // the active pointer id, once claimed
    var gestureScene = null;   // frozen scene snapshot for the gesture
    var gestureFit = null;
    var grabUser = null;       // pointer in viewBox coords at grab
    var grabBearing = 0;       // tilt: bearing at grab
    var startValue = 0;        // value at grab (drop for height, tilt for tilt)
    var lastRaw = null;        // last raw value emitted this gesture
    var rafId = 0;
    var pendingMove = null;    // {x, y} viewBox coords awaiting a frame
    // keyboard
    var keyPending = null;     // committed-on-keyup value of a running key gesture
    var keyIdle = 0;

    var listeners = [];
    function on(type, fn, opts) { el.addEventListener(type, fn, opts); listeners.push([type, fn, opts]); }

    function clearHold() { if (holdTimer) { window.clearTimeout(holdTimer); holdTimer = 0; } }
    function clearKeyIdle() { if (keyIdle) { window.clearTimeout(keyIdle); keyIdle = 0; } }
    function cancelFrame() { if (rafId) { window.cancelAnimationFrame(rafId); rafId = 0; } pendingMove = null; }

    function freezeGesture() {
      gestureScene = currentScene;
      gestureFit = currentScene && currentScene.fit;
    }

    function startDrag(pointerId, clientX, clientY) {
      clearHold();
      pendingId = null;
      dragId = pointerId;
      freezeGesture();
      if (!gestureFit) { dragId = null; return; }   // nothing to map against
      grabUser = clientToUser(svg, clientX, clientY);
      var v = sceneValue(kind, gestureScene);
      startValue = (v == null) ? 0 : v;
      lastRaw = startValue;
      if (kind === 'tilt') grabBearing = tiltDegFromPointer(grabUser.x, grabUser.y, gestureFit, gestureScene);
      try { el.setPointerCapture(pointerId); } catch (e) { /* capture is a nicety */ }
      el.classList.add('vmsw-elev__handle--grabbing');
      el.style.touchAction = 'none';                // scoped to this gesture only
    }

    function rawFromUser(u) {
      var lim = sceneLimits(kind, gestureScene);
      if (kind === 'slope') {
        return slopeDegFromPointer(u.x, u.y, gestureFit);
      }
      if (kind === 'tilt') {
        var bearing = tiltDegFromPointer(u.x, u.y, gestureFit, gestureScene);
        var delta = normalizeAngleDelta(bearing - grabBearing);
        return clampTilt(startValue + delta, lim.hi);
      }
      // height
      return clampHeadDrop(headDropFromPointer(u.y, grabUser.y, startValue, gestureFit), lim.lo, lim.hi);
    }

    function runMoveFrame() {
      rafId = 0;
      if (!pendingMove || dragId == null || !gestureFit) return;
      var lim = sceneLimits(kind, gestureScene);
      var raw = rawFromUser(pendingMove);
      pendingMove = null;
      lastRaw = raw;
      var pv = previewValue(kind, raw, lim);
      preview(kind, pv);
      nudgeAria(el, kind, pv);
    }

    function scheduleMove(clientX, clientY) {
      pendingMove = clientToUser(svg, clientX, clientY);
      if (rafId) return;
      rafId = window.requestAnimationFrame(runMoveFrame);
    }

    function endDrag() {
      if (dragId == null) return;
      try { el.releasePointerCapture(dragId); } catch (e) { /* already gone */ }
      dragId = null;
      cancelFrame();
      el.classList.remove('vmsw-elev__handle--grabbing');
      el.style.touchAction = '';
    }

    // The slop test needs the pointerdown origin, kept while the touch is pending.
    var pendOrigin = null;

    function down(e) {
      if (e.button != null && e.button > 0) return;         // right / middle
      if (dragId != null) return;
      if (!currentScene || !currentScene.fit) return;
      try { el.focus({ preventScroll: true }); } catch (err) { el.focus(); }
      // [TOUCH-FIX 2026-08-30] A pointerdown on a handle is a drag, claimed IMMEDIATELY for touch as well as
      // mouse/pen. The old hold/slop pending dance (wait TOUCH_HOLD_MS, then require under TOUCH_SLOP_PX of
      // travel) is retired: a normal finger drag moves more than a few px inside 130ms, so it read as a
      // scroll and the pill highlighted but never moved (the owner's iPhone report). The pills are small, so
      // a touch that starts on one is always a drag; touches elsewhere on the SVG still scroll (their
      // touchstart never reaches this handle). The non-passive touchstart handler is the reliable iOS scroll
      // trap at sequence start; the preventDefault here only handles mouse/pen text selection.
      if (e.pointerType !== 'touch' && e.cancelable) e.preventDefault();
      startDrag(e.pointerId, e.clientX, e.clientY);
    }

    function move(e) {
      if (pendingId != null && dragId == null) {
        if (pendingId !== e.pointerId || !pendOrigin) return;
        var travel = Math.abs(e.clientY - pendOrigin.y) + Math.abs(e.clientX - pendOrigin.x);
        if (travel > TOUCH_SLOP_PX) { clearHold(); pendingId = null; pendOrigin = null; }  // a scroll
        return;
      }
      if (dragId == null || dragId !== e.pointerId) return;
      scheduleMove(e.clientX, e.clientY);
    }

    function up(e) {
      if (pendingId != null && pendingId === e.pointerId) { clearHold(); pendingId = null; pendOrigin = null; }
      if (dragId == null || dragId !== e.pointerId) return;
      var lim = sceneLimits(kind, gestureScene);
      var raw = (lastRaw == null) ? startValue : lastRaw;
      var hadFocus = (document.activeElement === el);
      endDrag();
      if (hadFocus) refocusId = HANDLE_IDS[kind];
      commit(kind, commitValue(kind, raw, lim));            // last: this may replace el
    }

    function cancel(e) {
      clearHold();
      if (pendingId === e.pointerId) { pendingId = null; pendOrigin = null; }
      if (dragId == null || dragId !== e.pointerId) return;
      // Restore the pre-drag value through the preview; do NOT commit.
      var v = sceneValue(kind, gestureScene);
      endDrag();
      if (v != null) preview(kind, v);
    }

    function touchstart(e) {
      // [TOUCH-FIX 2026-08-30] Stop a page scroll from a touch that STARTS on the handle. Bound to the
      // handle element, so it fires only for handle touches; touches elsewhere on the SVG never reach here
      // and still scroll. Non-passive so preventDefault is allowed. This is the reliable iOS scroll trap at
      // sequence start, since WebKit's touch-action on an SVG element cannot be relied on.
      if (e.cancelable) e.preventDefault();
    }

    function touchmove(e) {
      // Non-passive; fires preventDefault once the gesture is claimed (it is claimed at pointerdown now).
      // With touchstart already trapping the scroll, this keeps the page pinned through the whole drag.
      if (dragId != null) e.preventDefault();
    }

    // ---- keyboard --------------------------------------------------------
    function startKeyRun() {
      clearKeyIdle();
      freezeGesture();
      keyIdle = window.setTimeout(function () { endKeyRun(); }, KEY_IDLE_MS);
    }
    function endKeyRun() {
      clearKeyIdle();
      if (keyPending == null) return;
      var lim = sceneLimits(kind, gestureScene || currentScene);
      var v = keyPending;
      keyPending = null;
      var hadFocus = (document.activeElement === el);
      if (hadFocus) refocusId = HANDLE_IDS[kind];
      commit(kind, commitValue(kind, v, lim));
    }

    function keydown(e) {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      if (!currentScene || !currentScene.fit) return;
      var lim = sceneLimits(kind, currentScene);
      var arrow = stepDef.arrow, page = stepDef.page;
      var base = (keyPending != null) ? keyPending : sceneValue(kind, currentScene);
      if (base == null) base = lim.lo === -Infinity ? 0 : lim.lo;
      var raw = base, commitNow = false, handled = true;
      switch (e.key) {
        // Height: DOWN/RIGHT increase the drop (lower on screen, bigger number).
        // Slope/tilt: UP/RIGHT increase the angle.
        case 'ArrowDown': case 'Down':
          raw = (kind === 'height') ? base + arrow : base - arrow; break;
        case 'ArrowUp': case 'Up':
          raw = (kind === 'height') ? base - arrow : base + arrow; break;
        case 'ArrowRight': case 'Right':
          raw = base + arrow; break;
        case 'ArrowLeft': case 'Left':
          raw = base - arrow; break;
        case 'PageDown':
          raw = (kind === 'height') ? base + page : base - page; break;
        case 'PageUp':
          raw = (kind === 'height') ? base - page : base + page; break;
        case 'Home': raw = lim.lo; commitNow = true; break;   // slope/tilt 0; height highest
        case 'End':  raw = (lim.hi === Infinity ? base : lim.hi); commitNow = true; break;
        default: handled = false;
      }
      if (!handled) return;
      e.preventDefault();
      if (commitNow) {
        clearKeyIdle();
        keyPending = null;
        var hf = (document.activeElement === el);
        if (hf) refocusId = HANDLE_IDS[kind];
        commit(kind, commitValue(kind, raw, lim));
        return;
      }
      // A run of arrow/page presses is ONE gesture: preview now, commit on keyup.
      if (keyPending == null) startKeyRun(); else if (keyIdle) { window.clearTimeout(keyIdle); keyIdle = window.setTimeout(function () { endKeyRun(); }, KEY_IDLE_MS); }
      var pv = previewValue(kind, raw, lim);
      keyPending = pv;
      preview(kind, pv);
      nudgeAria(el, kind, pv);
    }

    function keyup(e) {
      if (e.key === 'Shift') return;
      if (keyPending == null) return;
      endKeyRun();
    }

    function blur() {
      // Abandon an uncommitted key run silently (matches the elevation scene): no
      // emit, just drop the freeze and any pending touch.
      clearKeyIdle();
      keyPending = null;
      clearHold();
      pendingId = null;
      pendOrigin = null;
    }

    on('pointerdown', down);
    on('pointermove', move);
    on('pointerup', up);
    on('pointercancel', cancel);
    on('touchstart', touchstart, { passive: false });
    on('touchmove', touchmove, { passive: false });
    on('keydown', keydown);
    on('keyup', keyup);
    on('blur', blur);

    return function teardown() {
      clearHold();
      clearKeyIdle();
      cancelFrame();
      for (var i = 0; i < listeners.length; i++) {
        el.removeEventListener(listeners[i][0], listeners[i][1], listeners[i][2]);
      }
      listeners = [];
    };
  }

  // ---- attach / re-attach ------------------------------------------------
  function detachAll() {
    for (var i = 0; i < teardowns.length; i++) {
      try { teardowns[i](); } catch (e) { /* nothing to do */ }
    }
    teardowns = [];
  }

  function attach(scene) {
    detachAll();
    currentScene = scene || null;
    if (!currentScene || !currentScene.svgId || !currentScene.fit) return;
    var svg = document.getElementById(currentScene.svgId);
    if (!svg) return;
    ['slope', 'tilt', 'height'].forEach(function (kind) {
      var el = document.getElementById(HANDLE_IDS[kind]);
      if (!el) return;                     // e.g. tilt handle absent when tiltMax is 0
      teardowns.push(wireHandle(kind, el, svg));
    });
    // Pay back a focus owed from a commit that replaced the previously focused
    // handle element.
    if (refocusId) {
      var want = document.getElementById(refocusId);
      refocusId = null;
      if (want) { try { want.focus({ preventScroll: true }); } catch (e) { want.focus(); } }
    }
  }

  // Every render replaces the handle elements; re-attach against the fresh scene.
  ROOT.addEventListener('vmsw:rendered', function (e) {
    attach(e && e.detail ? e.detail : (api() && api().getScene ? api().getScene() : null));
  });

  // First attach if the answer (and its SVG) already exist by the time we load.
  function initialAttach() {
    var a = api();
    if (a && typeof a.getScene === 'function') {
      var scene = a.getScene();
      if (scene && scene.svgId && document.getElementById(scene.svgId)) attach(scene);
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialAttach);
  } else {
    initialAttach();
  }
})();
